import { useContext, useEffect, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useExpressReveal } from '@/context/ExpressRevealContext';
import { EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS, useExpressReadyTimeout } from '@/hooks/useExpressReadyTimeout';
import type { useExpressComponents } from '@/hooks/useExpressComponents';
import { trackCheckoutPresented } from '@/utils/checkoutMixpanelTracking';
import { getSessionIdFromUrlParams } from '@/utils/location';
import { getPresentedPaymentMethods } from '@/utils/presentedPaymentMethods';

/**
 * Fires CHECKOUT_PRESENTED once the screen has stopped changing shape, and
 * reports the payment methods the shopper can actually see.
 *
 * "Stopped changing shape" is not first render: express wallets resolve
 * asynchronously, and until they do we can't tell an Apple Pay button from an
 * Apple Pay button that is about to collapse — or, on PREFERRED, from a whole
 * template that is about to fall back to DEFAULT. Two arms cover the two
 * shapes a template can take:
 *
 *   - templates with an express row (DEFAULT / EMBEDDED_VIEW) wait for every
 *     wallet slot to report; a template with no wallets configured is settled
 *     immediately, since there is nothing to wait for.
 *   - single-CTA templates (PREFERRED / SINGLE) wait for the host view to lift
 *     its wallet gate. PREFERRED with a working wallet lifts it and reports
 *     that one method; PREFERRED with a broken wallet never lifts it, swaps to
 *     DEFAULT, and gets reported by the first arm on the remount instead.
 *
 * A hung PSP SDK would otherwise mean the event never fires at all, so the
 * gate's own hang budget bounds the wait and we report whatever settled.
 */
export const useTrackCheckoutPresented = (expressState: ReturnType<typeof useExpressComponents>): void => {
  const context = useContext(CheckoutContext);
  const { template, paymentMethodsAvailable, paymentMethodSelected } = context;
  const { isAllComponentsLoaded, availableExpressMethods } = expressState;
  const reveal = useExpressReveal();
  const sessionId = getSessionIdFromUrlParams();
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const hasTrackedRef = useRef(false);
  // Read fresh context for the event payload without re-running the effect on
  // every unrelated context change.
  const contextRef = useRef(context);
  contextRef.current = context;

  // No reveal host means no one is gating this screen — SINGLE (`OnePaymentView`)
  // and PREFERRED's desktop `DefaultView` fallback both render outside the
  // provider — so there is nothing to wait for and the screen is settled as it
  // stands. Treating the absent host as "still gating" would hold every one of
  // those sessions for the full hang budget below and inflate `load_time` by it.
  const isSettled = template.features.showExpressCheckout ? isAllComponentsLoaded : (reveal?.isRevealed ?? true);

  useExpressReadyTimeout(!isSettled, () => setHasTimedOut(true), EXPRESS_WALLET_GATE_HANG_TIMEOUT_MS);

  useEffect(() => {
    if (hasTrackedRef.current) return;
    if (!isSettled && !hasTimedOut) return;
    hasTrackedRef.current = true;

    trackCheckoutPresented(
      sessionId,
      contextRef.current,
      getPresentedPaymentMethods({
        template,
        paymentMethodsAvailable,
        paymentMethodSelected,
        availableExpressMethods,
      })
    );
  }, [
    isSettled,
    hasTimedOut,
    sessionId,
    template,
    paymentMethodsAvailable,
    paymentMethodSelected,
    availableExpressMethods,
  ]);
};
